import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';

/** Determines default landing page based on user type */
const useDefaultRoute = () => {
  const user = useSelector(getUser);
  const isPartnerLinked = useSelector(getIsPartnerLinked);
  return user?.isCourier && isPartnerLinked ? '/warehouse' : '/mailbox';
};

/** Redirects to the appropriate default page based on user type */
const DefaultRedirect: React.FC = () => {
  const defaultRoute = useDefaultRoute();
  return <Navigate to={defaultRoute} replace />;
};

/** Auth page wrapper that redirects to ?redirect= param after login, or default route */
const AuthRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const defaultRoute = useDefaultRoute();
  const redirect = searchParams.get('redirect') || defaultRoute;
  return <Navigate to={redirect} replace />;
};

/** Renders courier-specific or regular packages page based on user type */
const CourierPackagesRoute: React.FC = () => {
  const user = useSelector(getUser);
  const isPartnerLinked = useSelector(getIsPartnerLinked);
  return user?.isCourier && isPartnerLinked ? <CourierPackages /> : <Packages />;
};
import DashboardLayout from '../components/dashboard/DashboardLayout';
import Mailbox from '../pages/Mailbox';
import Packages from '../pages/Packages';
import PackageDetail from '../pages/PackageDetail';
import AddPackage from '../pages/AddPackage';
import DeliveryRequest from '../pages/DeliveryRequest';
import LocalDelivery from '../pages/LocalDelivery';
import Calculator from '../pages/Calculator';
import Referrals from '../pages/Referrals';
import Profile from '../pages/Profile';
import More from '../pages/More';
import Reviews from '../pages/Reviews';
import ReportProblem from '../pages/ReportProblem';
import ContactUs from '../pages/ContactUs';
import Invoices from '../pages/Invoices';
import InvoiceDetail from '../pages/InvoiceDetail';
import ShopForMe from '../pages/ShopForMe';
import ShopLayout from '../components/shop/ShopLayout';
import ShopBrowse from '../pages/shop/ShopBrowse';
import ShopCart from '../pages/shop/ShopCart';
import ShopOrders from '../pages/shop/ShopOrders';
import ProductDetail from '../pages/shop/ProductDetail';
import OrderConfirmation from '../pages/shop/OrderConfirmation';
import { AuthPage } from '../auth/components';
import { ResetPassword } from '../auth/pages';
import { useDispatch, useSelector } from "react-redux";
import { getIsCourierLoading, getCourierError, getCourier, getCourierSetting, checkPartnerLinked, getIsPartnerLinked } from '../state/courierSlice';
import { AppDispatch } from "../store";
import { fetchCourier } from '../state/courierSlice';
import { getIsAuthenticated, getUser, init } from '../state/authSlice';
import { Loader, LoaderCircle, Text } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { DarkModeProvider } from '../context/DarkModeContext';
import { CartProvider } from '../context/CartContext';
import AuthorizedUsers from '../pages/AuthorizedUsers';
import WarehouseDashboard from '../pages/WarehouseDashboard';
import CourierPackages from '../pages/CourierPackages';
import { useTheme } from '../hooks/useTheme';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { useAnalyticsContext } from '../utils/track';
import TrnRequirementModal from '../components/TrnRequirementModal';
import PasskeyEnrolmentPrompt from '../auth/components/PasskeyEnrolmentPrompt';
import ReviewPrompt from '../components/reviews/ReviewPrompt';

export function App() {

  const dispatch = useDispatch<AppDispatch>();
  const isCourierLoading = useSelector(getIsCourierLoading);
  const courierError = useSelector(getCourierError);
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isPartnerLinked = useSelector(getIsPartnerLinked);
  const {setThemeColors} = useTheme();

  // Check for app updates and show toast when new version is available
  useVersionCheck();

  // Keep the GTM dataLayer analytics context (tenant + user dimensions) in sync
  useAnalyticsContext();


  useEffect(() => {
    // Initialize auth state from localStorage
    // console.log('init app');
    // console.log('isAuthenticated', isAuthenticated);
    dispatch(init());
    // Fetch courier data
    dispatch(fetchCourier());
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (courier?.themeSetting) setThemeColors(courier.themeSetting);
    if (courier?.id && isAuthenticated) {
      dispatch(checkPartnerLinked(String(courier.id)));
    }
  }, [courier, setThemeColors, dispatch, isAuthenticated]);

  
  if (isCourierLoading) {
    return <div className="flex flex-col justify-center items-center h-screen gap-4">
      <h3 className="text-2xl font-bold">Loading courier data...</h3>
      <LoaderCircle className="animate-spin h-20 w-20" />
    </div>;
  }

  if (courierError) {
    return <div className="flex flex-col justify-center items-center h-screen gap-4">
      <h3 className="text-2xl font-bold">Oops! Something went wrong</h3>
      {/* Add svg error icon */}
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-20">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>

      <p className="text-red-500">You may need to refresh the page or try again later.</p>
    </div>;
  }

  return (
    <DarkModeProvider>
      <CartProvider>
        <Router>
          <Toaster position="top-right" />
          {isAuthenticated && <TrnRequirementModal />}
          {isAuthenticated && <PasskeyEnrolmentPrompt />}
          {isAuthenticated && <ReviewPrompt />}
          <Routes>
          {/* Public routes that don't require authentication */}
          <Route path="/auth/reset-password/:accessToken" element={<ResetPassword />} />

          {/* Auth routes */}
          <Route path="/auth" element={!isAuthenticated ? <AuthPage /> : <AuthRedirect />} />
          
          {/* Public shop routes — browsing, product detail, and cart don't require auth */}
          {courierSetting?.isShopEnabled === true ? (
            <Route path="/shop" element={<ShopLayout />}>
              <Route index element={<ShopBrowse />} />
              <Route path="product/:id" element={<ProductDetail />} />
              <Route path="cart" element={<ShopCart />} />
              <Route path="checkout" element={<ShopCart />} />
              <Route path="order-confirmation/:orderId" element={<OrderConfirmation />} />
              <Route path="orders" element={isAuthenticated ? <ShopOrders /> : <Navigate to={`/auth?redirect=${encodeURIComponent('/shop/orders')}`} replace />} />
            </Route>
          ) : (
            <Route path="/shop/*" element={<Navigate to={isAuthenticated ? "/packages" : "/auth"} replace />} />
          )}

          {/* Protected routes that require authentication */}
          {isAuthenticated ? (
            <>
              <Route path="/" element={<DashboardLayout />}>
                <Route index element={<DefaultRedirect />} />
                <Route path="mailbox" element={<Mailbox />} />
                <Route path="authorized-users" element={<AuthorizedUsers />} />
                <Route path="packages" element={<CourierPackagesRoute />} />
                <Route path="packages/:id" element={<PackageDetail />} />
                <Route path="packages/add" element={<AddPackage />} />
                <Route path="delivery-request" element={<DeliveryRequest />} />
                <Route path="local-delivery" element={courierSetting?.isLocalDeliveryEnabled === true ? <LocalDelivery /> : <Navigate to="/packages" replace />} />
                <Route path="calculator" element={<Calculator />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="warehouse" element={isPartnerLinked ? <WarehouseDashboard /> : <Navigate to="/packages" replace />} />
                <Route path="shop-for-me" element={courierSetting?.isShopForMeEnabled === true ? <ShopForMe /> : <Navigate to="/packages" replace />} />
                <Route path="profile" element={<Profile />} />
                <Route path="more" element={<More />} />
                <Route path="reviews" element={courierSetting?.isReviewsEnabled === true ? <Reviews /> : <Navigate to="/packages" replace />} />
                <Route path="report-problem" element={<ReportProblem />} />
                <Route path="contact-us" element={<ContactUs />} />
              </Route>

              {/* Catch any unknown routes and redirect to default page */}
              <Route path="*" element={<DefaultRedirect />} />
            </>
          ) : (
            // When not authenticated, redirect any unknown routes to auth
            <Route path="*" element={<Navigate to="/auth" replace />} />
          )}
        </Routes>
        </Router>
      </CartProvider>
    </DarkModeProvider>
  );
}
